import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Section, Field } from "../../shared/components/EditorFields"
import { EntityPicker } from "./pickers"
import { Badge } from "@/components/ui/badge"
import { type ServiceStore, type EntityKind, displayName, PROVIDER_STATUSES } from "../types"
import * as db from "../store"

export function ProviderFields({
  store, locale, provider, set, onSelect,
}: {
  store: ServiceStore
  locale: string
  provider: any
  set: (patch: Record<string, any>) => void
  onSelect: (kind: EntityKind, id: string) => void
}) {
  const services = db.servicesOfProvider(store, provider.id)
  return (
    <>
      <Section title="Organization" defaultOpen>
        <Field label="Status">
          <Select value={provider.status} onValueChange={v => set({ status: v })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROVIDER_STATUSES.map(s => (
                <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Country" hint="Required in Directus">
          <EntityPicker
            store={store} locale={locale} kind="country" options={db.listEntities(store, "country")}
            value={provider.countryId} onChange={v => set({ countryId: v })}
            placeholder="No country"
          />
        </Field>
        <Field label="Provider category">
          <EntityPicker
            store={store} locale={locale} kind="providerCategory" options={db.listEntities(store, "providerCategory")}
            value={provider.categoryId} onChange={v => set({ categoryId: v })}
            placeholder="No category"
          />
        </Field>
        <Field label="Expiration" hint="When this provider's record should be reviewed">
          <Input
            type="date" value={provider.expiration ?? ""}
            onChange={e => set({ expiration: e.target.value })}
            className="h-8 text-sm"
          />
        </Field>
      </Section>

      <Section title={`Services (${services.length})`} defaultOpen={services.length > 0}>
        {services.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No services yet. A provider with no services is invisible to users — add one from the browser.
          </p>
        ) : (
          <div className="space-y-1">
            {services.map((s: any) => (
              <button
                type="button"
                key={s.id}
                onClick={() => onSelect("service", s.id)}
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-muted/60"
              >
                <span className="truncate flex-1">{displayName(s, locale, store.defaultLocale)}</span>
                <Badge variant="secondary" className="shrink-0 text-[10px]">{s.status}</Badge>
              </button>
            ))}
          </div>
        )}
      </Section>
    </>
  )
}

// ── Primitives ───────────────────────────────────────────────────────────────
// Section / Field / ToggleField live in ../components/EditorFields so this
// editor and SiteConfigEditor cannot drift apart.

/** Single-value FK picker with a clear option. */
